import type { DemandSource } from './demandGrouping';

// 'counter' is not a sales channel — it is the 門市 rail asking for stock. It shares this
// union because 出貨人員 unpack one pile of boxes and must see everything they have to
// receive in one queue; it is kept visually unmistakable (its own colour, its own label,
// no customer row) because the ONE thing they must never do is post a counter parcel to a
// customer. Magnus 2026-09-18: 「will be for 門市 or else 出貨人員 will not know if it is
// 門市 or for what people or order」.
export type Platform = 'shopee' | 'shopify' | 'storefront' | 'counter';

/** A 門市 order has no customer, no address and no waybill. Read this, not `platform === 'counter'`. */
export const isCounterOrder = (o: { platform: Platform }): boolean => o.platform === 'counter';

export type { DemandSource };

export interface UnifiedOrderItem {
  id: string;
  productName: string;
  variantOrSpec: string;
  sku: string;
  quantity: number;
  imageUrl: string;
  received: boolean;
  missingCount: number;
  outOfStock?: boolean;
  // Shopee only: size changed in the OMS (shopee_tracking.spec_override). variantOrSpec stays
  // the buyer's original model_name — it is the tracking row key, so never overwrite it.
  specOverride?: string | null;
  note: string;
  tag: string;
  referenceImage?: string;
  receivedBy?: string;
  receivedAt?: string;
  // Present ONLY on 門市 lines. `receivedQty` may be less than `quantity` — an arrival
  // short-shipped to this line because an older order took the pieces (allocation is by
  // earliest ordered_at, Magnus 2026-09-18). The gap is rendered as 短少 on the card.
  counterLine?: {
    lineId: number;
    variantId: number;
    orderedAt: string;
    receivedQty: number | null;
    remainingQty?: number;
    cancelledQty?: number;
  };
  originalItem: any;
}

/** The 門市 parcel behind a counter card. There is no customer, so there is no customer block. */
export interface CounterOrderInfo {
  id: number;
  clientRef: string;
  label: string | null;
  note: string | null;
  createdBy: string | null;
  /** 進貨店家 on this parcel, first-seen order — the nearest thing to a "who is this from". */
  sourceTags: string[];
  piecePlanned: number;
  pieceReceived: number;
  pieceOpen: number;
  /** Sum of (ordered − received) over lines already ticked. >0 = this parcel came up short. */
  pieceShort: number;
  cancelledAt: string | null;
}

// 貨架格 (receiving bin) info for one order, derived from GET /api/receiving/slots.
// `slots` = the distinct bin numbers this order's received items currently occupy.
// `stale` = an item has dwelled longer than the configured threshold (default 14 days).
// `items` maps `${productName}|||${variantSpec}` → bin number for the per-row chip.
export interface OrderSlotInfo {
  slots: number[];
  stale: boolean;
  staleDays: number;
  items: Map<string, number>;
}

export interface UnifiedOrder {
  id: string;
  platform: Platform;
  // Which side of the counter this demand belongs to. Absent = a customer order,
  // which is every order that existed before 門市 orders did. The 門市 builder sets
  // 'counter'; from there on the grouping in demandGrouping.ts keeps the two apart
  // by construction (Magnus 2026-09-15: 「don't merge」).
  demandSource?: DemandSource;
  orderId: string;
  orderNumber: string;
  // OPTIONAL since 門市 orders. A 門市 parcel has no customer at all, and the page used to
  // call `order.customerName.toLowerCase()` in three search paths — the crash was one
  // counter card away. Optional rather than `''` on purpose: the empty string would have
  // type-checked and thrown nothing, leaving every consumer free to assume a customer
  // exists. Read it through a `|| ''` or not at all.
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  orderDate: Date;
  orderDateDisplay: string;
  status: string;
  deliveryMethod: string;
  storeId?: string;
  storeName?: string;
  storeAddress?: string;
  shippingAddress?: string;
  totalAmount?: number;
  paymentMethod?: string;
  paymentStatus?: string;
  trackingNumber?: string;
  paymentNo?: string;
  cvsValidationNo?: string;
  ecpayLogisticsId?: string;
  logisticsSubType?: string;
  shippingCarrier?: string;
  hasTrackingInfo?: boolean;
  // Session-only flag set right after a ship action (own click or socket).
  // Lets the order stay in the 未收到/可以出貨 filter view until refresh, so the user can
  // confirm the tracking number appeared before the order moves to 已出貨.
  justShipped?: boolean;
  items: UnifiedOrderItem[];
  receivedCount: number;
  totalCount: number;
  allReceived: boolean;
  original: any;
  // Exchange-order enrichment (populated for Shopify exchange orders with ID /^9\d{7}$/)
  exchangeRequest?: ExchangeRequestInfo;
  // Storefront 補寄 history — one entry per reship attempt, earliest first
  reships?: ReshipInfo[];
  // Pin (需注意): order-level attention marker with an optional free-text note.
  // Shared across warehouse stations; a filter surfaces only pinned orders.
  pinned?: boolean;
  pinNote?: string;
  pinnedBy?: string;
  pinnedAt?: string;
  // Set exactly when platform === 'counter'.
  counterOrder?: CounterOrderInfo;
}

/**
 * A 門市 order is DONE when every line has been ticked — there is no waybill to wait for,
 * so `allReceived && !hasTracking` (the 可以出貨 test) would park it in 可以出貨 for ever.
 * Both the card and the page ask this instead.
 */
export const isReadyToShipOrder = (o: UnifiedOrder): boolean =>
  o.platform !== 'counter' && o.allReceived && !o.hasTrackingInfo;

/** 門市 parcels are fully unpacked and stocked — nothing left on this page to do. */
export const isCounterComplete = (o: UnifiedOrder): boolean =>
  o.platform === 'counter' && (!!o.counterOrder?.cancelledAt || o.allReceived);

export interface ReshipInfo {
  reshipOrderId: string;
  trackingNumber: string | null;
  paymentNo?: string | null;
  validationNo?: string | null;
  ecpayLogisticsId?: string | null;
  storeId?: string | null;
  storeName?: string | null;
  logisticsSubType?: string | null;
  status?: string | null;
  createdAt: string;
  // SF reships set these. 'sf' = SF International (HK/MO), 'sf_tw' = 順豐台灣宅配.
  carrier?: 'cvs' | 'sf' | 'sf_tw';
  labelUrl?: string | null;
  invoiceUrl?: string | null;
  customerOrderNo?: string | null;
}

export interface ExchangeRequestInfo {
  requestId: string;
  originalOrderNumber: string;
  newOrderId: string;
  requestType: 'exchange';
  status: string;
  returnTrackingNumber?: string;
  returnPaymentNo?: string;
  returnValidationNo?: string;
  returnLogisticsId?: string;
  logisticsStatus?: string;
  logisticsStatusName?: string;
  logisticsStatusUpdatedAt?: string;
  createdAt?: string;
  customerShippedReturn: boolean;
}

// Callbacks that the page passes to the card
export interface OrderCardCallbacks {
  onToggleReceived: (order: UnifiedOrder, item: UnifiedOrderItem, received: boolean) => void;
  onUpdateField: (order: UnifiedOrder, item: UnifiedOrderItem, field: string, value: any) => void;
  onShip: (order: UnifiedOrder) => void;
  onPrintLabel: (order: UnifiedOrder) => void;
  onQuickShip?: (order: UnifiedOrder) => void;
  onHsinchuShip?: (order: UnifiedOrder) => void;
  onReship?: (order: UnifiedOrder) => void;
  onOutOfStockNotify?: (order: UnifiedOrder) => void;
  onRefund?: (order: UnifiedOrder) => void;
  onCancelOrder?: (order: UnifiedOrder) => void;
  onRevertShipped?: (order: UnifiedOrder) => void;
  onFulfill?: (order: UnifiedOrder) => void;
  onToggleOutOfStock?: (order: UnifiedOrder, item: UnifiedOrderItem, outOfStock: boolean) => void;
  onVariantUpdate?: (order: UnifiedOrder, item: UnifiedOrderItem, newVariant: string) => void;
  onSkuUpdate?: (order: UnifiedOrder, item: UnifiedOrderItem, newSku: string) => void;
  onAddressUpdate?: (order: UnifiedOrder, newAddress: string) => void;
  onCustomerUpdate?: (order: UnifiedOrder, name: string, phone: string) => void;
  getProductLink?: (sku: string) => { link?: string; allLinks?: string[] };
  showLinks?: boolean;
  onStorefrontShipped?: (order: UnifiedOrder) => void;
  // Pin toggle / note edit. pinned=false unpins; pinned=true upserts with the note.
  onTogglePin?: (order: UnifiedOrder, pinned: boolean, note?: string) => void;
  onResetSlot?: (order: UnifiedOrder) => void;
  // Opens the 選色 plate (storefront flat lay + labelled colour markers) for one
  // line, so staff can name the colour of the garment in hand.
  onOpenColorPlate?: (item: UnifiedOrderItem) => void;
  // 門市 only. Stocks `qty` pieces of this line into noke_inventory as 未上架 and stamps
  // the line received. Deliberately NOT onToggleReceived: that one writes a tracking row
  // and a bin and moves no stock, which for a 門市 parcel would be the wrong thing twice.
  onCounterReceive?: (order: UnifiedOrder, item: UnifiedOrderItem, qty: number, location: 'office' | 'store') => void | Promise<void>;
  // 門市 only. Cancels the still-awaited remainder of ONE line (cancel_remainder) — pieces
  // already received stay in stock. Throw to keep the row and show the error.
  onCounterCancelLine?: (order: UnifiedOrder, item: UnifiedOrderItem) => Promise<void>;
  // 門市 only. Cancels everything still awaited on the parcel; received pieces stay.
  onCounterCancelOrder?: (order: UnifiedOrder) => Promise<void>;
}

/** Where one open line stands in the queue for its colour/size. See shortShipAllocation.ts. */
export interface QueuePosition {
  rank: number;
  /** Pieces wanted by lines that ordered EARLIER than this one. */
  aheadPieces: number;
  /** The earliest of those, for a 「ORD-123 先」 hint. */
  aheadLabel: string;
  queueSize: number;
}

// 門市 is deep violet: the three customer channels are orange / green / slate, so nothing
// on the page is near it. It is the only card colour that does not belong to a shop.
export const PLATFORM_COLORS: Record<Platform, string> = {
  shopee: '#EE4D2D',
  shopify: '#96BF48',
  storefront: '#4c5e8a',
  // Same grey-blue family as 官網, one step deeper (Magnus 2026-09-22: 「don't use purple
  // just use the same color palette grey blue」). 門市 stays tellable-apart by its 門市補貨
  // label and its 收貨 controls, not by a foreign colour.
  counter: '#2B3A4E',
};

export const PLATFORM_LABELS: Record<Platform, string> = {
  shopee: '蝦皮',
  shopify: 'Shopify',
  storefront: '官網',
  counter: '門市補貨',
};
